import React, { useState } from "react";
import { FaPlayCircle } from "react-icons/fa";

// Helper to extract YouTube video ID from various URL formats
const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

export default function EditVideoTopic({ formData, setFormData, errors }) {
  const [showPreview, setShowPreview] = useState(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!formData) {
    return null;
  }

  const youtubeId = extractYouTubeId(formData.videoUrl);
  const isDirectVideo = (formData.videoUrl || "").match(/\.(mp4|webm|ogg)(\?.*)?$/i);

  return (
    <div className="space-y-6 border-t pt-8">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-pink-100 rounded-lg">
          <FaPlayCircle className="text-pink-600 text-xl" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Video Content</h3>
          <p className="text-sm text-gray-500">Edit the YouTube link or direct video URL</p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Video URL *
        </label>
        <input
          type="url"
          name="videoUrl"
          value={formData.videoUrl}
          onChange={handleInputChange}
          placeholder="e.g., https://youtube.com/watch?v=..."
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all ${
            errors.videoUrl ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.videoUrl && (
          <p className="text-red-600 text-sm mt-2">{errors.videoUrl}</p>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Supports YouTube URLs (watch, shorts, embed) and direct MP4/WebM links
        </p>
      </div>

      {/* Video Preview */}
      {formData.videoUrl && showPreview && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Preview</span>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Hide preview
            </button>
          </div>

          <div className="border border-gray-200 rounded-xl overflow-hidden bg-black aspect-video">
            {youtubeId ? (
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title="Video Preview"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : isDirectVideo ? (
              <video
                src={formData.videoUrl}
                controls
                className="w-full h-full"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center p-4">
                  <FaPlayCircle className="text-4xl mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Enter a valid YouTube or video URL to preview</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {formData.videoUrl && !showPreview && (
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="text-sm text-pink-600 hover:text-pink-700 font-medium"
        >
          Show preview
        </button>
      )}
    </div>
  );
}
